import { useEffect, useState } from 'react'

import ComponentLoader from '@scoir/components-core/dist/components/ComponentLoader'
import qs from '@scoir/query-string'
import { format, parseISO } from 'date-fns'
import { FormikHelpers } from 'formik'

import api from 'common/api'
import { type AuthToken, type ConfirmResponse } from 'common/auth'
import { clearLocalStorage, routeToApp, setCookies } from 'common/login-service'
import { useClearClickTrackingQueryParams, useClickTrackingQueryParams } from 'components/QueryParamsProvider'
import RegistrationForm, { RegistrationFormValues } from 'components/RegistrationForm'
import CountryMappingProvider from 'components/RegistrationForm/CountryMappingProvider'
import { isValidDate } from 'components/RegistrationForm/helper'
import Title from 'components/Title'

const StudentRegisterRoute = ({ token }: { token: string }) => {
  const [tokenAuth, setTokenAuth] = useState<AuthToken | undefined>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [confirmError, setConfirmError] = useState(false)
  const [initialValues, setInitialValues] = useState<RegistrationFormValues | undefined>()
  const [hasOtherHighSchool, setHasOtherHighSchool] = useState(false)

  useEffect(() => {
    api
      .post<unknown, AuthToken>(`/api/student/register/invite/validate/${token}`, {})
      .then((auth) => {
        const bd = auth?.BirthDate
        const birthdate = bd && isValidDate(bd) ? parseISO(bd) : undefined

        const matchedHsStudent = auth?.MatchedHighSchoolStudent
        const gy = matchedHsStudent?.GraduationYear
        const gradYear = gy > 0 ? gy : new Date().getFullYear() + 1

        const isOtherSchool = Boolean(auth?.HighSchool?.HighSchoolName === 'Other')
        setHasOtherHighSchool(isOtherSchool)
        setTokenAuth(auth)
        setInitialValues({
          firstName: matchedHsStudent?.FirstName || '',
          lastName: matchedHsStudent?.LastName || '',
          password: {
            pass: '',
            passConfirm: '',
          },
          birthdate,
          gradYear,
          confirmTOS: false,
          recaptchaResponse: '',
          country: isOtherSchool ? 'USA' : '',
          city: '',
          stateOrProvince: '',
        })
        setLoading(false)
      })
      .catch((err) => {
        const errMsg =
          err.status === 401
            ? 'This Scoir Invitation is no longer valid.'
            : 'An unexpected error occurred, please try again later.'
        setError(errMsg)
        setLoading(false)
      })
  }, [token])

  const clickTrackingQueryParams = useClickTrackingQueryParams()
  const onClearClickTrackingQueryParams = useClearClickTrackingQueryParams()

  const registerStudentUser = (values: RegistrationFormValues, props: FormikHelpers<RegistrationFormValues>) => {
    props.setSubmitting(true)

    const body: { [key: string]: any } = {
      ...tokenAuth,
      HighSchool: tokenAuth?.HighSchool,
      FirstName: values.firstName,
      LastName: values.lastName,
      GraduationYear: values.gradYear,
      Category: 'High School Student',
      Password: {
        Pass: values.password.pass,
        PassConfirm: values.password.passConfirm,
      },
      RecaptchaResponse: values.recaptchaResponse,
      BirthDate: format(values.birthdate!, 'yyyy-MM-dd'),
      Country: values.country,
      City: values.city,
      State: values.stateOrProvince,
      IsPostGradStudent: tokenAuth?.IsPostGradStudent,
    }

    if (tokenAuth?.MatchedHighSchoolStudent?.HighSchoolStudentId) {
      body.HighSchoolStudentId = tokenAuth.MatchedHighSchoolStudent.HighSchoolStudentId
    }

    const params = qs.stringify({
      ...clickTrackingQueryParams,
      sessionId: window.localStorage.getItem('sessionId'),
    })
    api
      .post<unknown, ConfirmResponse>(`/api/student/confirm?${params}`, body)
      .then((loginResponse) => {
        if (loginResponse.access_token && loginResponse.UserTypes) {
          const userType = loginResponse.UserTypes[0]
          clearLocalStorage(userType)
          setCookies(userType, loginResponse.access_token)
          routeToApp(userType)
        } else {
          setConfirmError(true)
          props.setSubmitting(false)
        }
        onClearClickTrackingQueryParams()
      })
      .catch(() => {
        setConfirmError(true)
        props.setSubmitting(false)
      })
  }

  if (error) {
    return <Title subtitle={error} />
  }
  return (
    <CountryMappingProvider>
      <ComponentLoader loaded={!loading}>
        <Title
          title="Welcome to Scoir ✨"
          subtitle={`Please complete your registration ${tokenAuth?.Email ? `for ${tokenAuth.Email}` : ''}`}
        />
        <RegistrationForm
          initialValue={initialValues}
          handleSubmit={registerStudentUser}
          useCaptcha
          showLocation={hasOtherHighSchool}
          confirmError={confirmError}
        />
      </ComponentLoader>
    </CountryMappingProvider>
  )
}

export default StudentRegisterRoute
